import React, { useEffect } from 'react'
import { node } from 'prop-types'
import buildInfo from 'build-info.json'
import {
  __check_injected_params__,
  __initial_reset_cache__,
  __prod__,
  NAVI_TYPES,
  PAGE_TYPES,
} from 'settings/app/constants'
import {
  getAssetPath,
  getEntryPoint,
  getPageType,
  getCookieValueByName,
  setCookie,
} from 'settings/app/helpers'
import { checkEnvironmentVarsByProp, HEALTH_ENTRYPOINT } from './helper'
import { useDispatch, useSelector } from 'react-redux'
import {
  resetPreviouslyCacheState,
  setClientBuildSpecs,
  setModuleEntryPoint,
} from 'redux/actions/applicationActions'
import {
  resetSearchResults,
  setSearchQueryParam,
  setTypeSpecificSearchParams,
} from 'redux/actions/searchActions'

/**
 * check injected props (from typo given)
 * set entryPoint
 * reset previously search results
 * set seo params
 *
 * @param children
 * @returns {JSX.Element}
 * @constructor
 */
const EnvironmentSetter = ({ children }) => {
  const dispatch = useDispatch()
  const { application, search } = useSelector((state) => state)
  const { entryPoint: entryPointFromRedux } = application
  const { clientHasSearched } = search
  const { current: pageType } = React.useRef(getPageType())
  const { current: entryPointFromStorage } = React.useRef(getEntryPoint())
  const { current: assetPathFromStorage } = React.useRef(getAssetPath())
  const propTypesHasChecked = React.useRef(false)

  // 1.) client has searched and nav back to startPage
  // 2.) search typ has changed
  const resetClientCache =
    (pageType === PAGE_TYPES.START_PAGE && clientHasSearched) ||
    entryPointFromRedux !== entryPointFromStorage

  /**
   * 1.) injects props check (if localstorage flag is set [readme])
   * 2.) client reset and build specs
   */
  useEffect(() => {
    if (__prod__ && __check_injected_params__ && !propTypesHasChecked.current) {
      checkEnvironmentVarsByProp('application')
      if (pageType !== PAGE_TYPES.RESULT_PAGE) {
        checkEnvironmentVarsByProp('headerProps')
      }
      if (pageType === PAGE_TYPES.SEO_PAGE) {
        checkEnvironmentVarsByProp('seoPage')
      }
      propTypesHasChecked.current = true
    }

    // reset store on initial load
    if (
      getCookieValueByName('navigatorenInitialized') === null &&
      __initial_reset_cache__
    ) {
      const { revision } = buildInfo
      const buildTime = new Date(buildInfo.time).toUTCString()
      dispatch(resetPreviouslyCacheState({ revision, buildTime }))
      dispatch(setClientBuildSpecs({ revision, buildTime }))
    }

    // reset cache when previous search exists
    if (resetClientCache) {
      dispatch(resetSearchResults())
      dispatch(setTypeSpecificSearchParams(entryPointFromStorage))
    }

    // set entryPoint && assetPath && flag for app is initialized
    dispatch(
      setModuleEntryPoint(
        entryPointFromStorage,
        assetPathFromStorage,
        HEALTH_ENTRYPOINT.includes(entryPointFromStorage)
          ? NAVI_TYPES[0]
          : NAVI_TYPES[1]
      )
    )

    // use session cookie instead of session storage
    // session cookie is shared between tabs
    setCookie('navigatorenInitialized', 'true')
  }, [
    dispatch,
    assetPathFromStorage,
    entryPointFromStorage,
    entryPointFromRedux,
    pageType,
    resetClientCache,
  ])

  /**
   * set seo search props (pre-filled search params)
   * client will performe request on initial load
   */
  useEffect(() => {
    if (
      pageType === PAGE_TYPES.SEO_PAGE &&
      localStorage.getItem('searchQuery') !== '' &&
      localStorage.getItem('searchLocation') !== '' &&
      localStorage.getItem('searchRadius') !== '' &&
      localStorage.getItem('searchKey') !== '' &&
      localStorage.getItem('searchType') !== ''
    ) {
      dispatch(
        setSearchQueryParam({
          query: localStorage.getItem('searchQuery'),
          location: localStorage.getItem('searchLocation'),
          radius: parseInt(localStorage.getItem('searchRadius'), 10),
        })
      )
    }
  }, [pageType, dispatch])

  return <>{children}</>
}

EnvironmentSetter.propTypes = {
  children: node.isRequired,
}

export default EnvironmentSetter
